import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useState, useEffect, useRef } from "react";
import { catchAxiosError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { toast } from "sonner";
import { authRepo } from "@/lib/repositories";

const COUNTDOWN_SECONDS = 300;

const formSchema = z.object({
  code: z.string().min(1),
});

interface VerifyCodeFormProps {
  email: string;
  onSuccess: (code: string) => void;
  onBack: () => void;
}

const VerifyCodeForm = ({ email, onSuccess, onBack }: VerifyCodeFormProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setCountdown(COUNTDOWN_SECONDS);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const formatCountdown = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { code: "" },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setCodeError(null);
    setIsLoading(true);
    const [error, result] = await catchAxiosError(
      authRepo.verifyResetCode(email, data.code),
    );
    setIsLoading(false);
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    if (!result.valid) {
      setCodeError(t("modules.auth.forgot_password.code_invalid"));
      return;
    }
    onSuccess(data.code);
  }

  async function handleResend() {
    setIsResending(true);
    const [error] = await catchAxiosError(authRepo.requestPasswordReset(email));
    setIsResending(false);
    if (error) {
      toast.error(handleApiError(error).message);
      return;
    }
    startCountdown();
    toast.success(t("modules.auth.forgot_password.code_sent_message"));
  }

  return (
    <div className="w-full max-w-112.5 flex flex-col gap-12">
      <p className="text-sm text-muted-foreground">
        {t("modules.auth.forgot_password.code_sent_message")}
      </p>
      <form id="verify-code-form" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup>
          <Controller
            name="code"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid || !!codeError}>
                <FieldLabel htmlFor="code-input">
                  {t("modules.auth.forgot_password.code_label")}
                </FieldLabel>
                <Input
                  {...field}
                  className="h-12"
                  id="code-input"
                  type="text"
                  autoComplete="one-time-code"
                  aria-invalid={fieldState.invalid || !!codeError}
                  onChange={(e) => {
                    field.onChange(e);
                    setCodeError(null);
                  }}
                />
                {(fieldState.invalid || codeError) && (
                  <FieldError
                    errors={
                      codeError ? [{ message: codeError }] : [fieldState.error]
                    }
                  />
                )}
              </Field>
            )}
          />
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {countdown > 0
                ? `${t("modules.auth.forgot_password.code_expires_in")} ${formatCountdown(countdown)}`
                : null}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isResending}
              onClick={handleResend}
              className="text-blue-600 underline p-0 h-auto"
            >
              {isResending && <Loader className="animate-spin size-3 mr-1" />}
              {t("modules.auth.forgot_password.resend_code")}
            </Button>
          </div>
        </FieldGroup>
      </form>
      <div className="flex flex-col gap-4">
        <Button
          disabled={isLoading}
          type="submit"
          form="verify-code-form"
          className="h-16 text-lg"
        >
          {isLoading && <Loader className="animate-spin" />}
          {t("modules.auth.forgot_password.submit_verify")}
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="underline text-blue-600 text-center text-sm cursor-pointer"
        >
          {t("modules.auth.forgot_password.back")}
        </button>
      </div>
    </div>
  );
};

export default VerifyCodeForm;
